import { useEffect, useMemo, useState } from 'react';
import {
  ChevronRight,
  File,
  FileImage,
  Folder,
  HardDrive,
  Loader2,
  Search,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { globalVFS } from '@/OS/utils/storage/VFS';
import type { FileMetadata } from '@/OS/utils/storage/StorageDriver';
import { cn } from '@/lib/utils';

interface MapleOSFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectFile: (file: FileMetadata) => void;
  selecting?: boolean;
}

type PickerRow =
  | {
      kind: 'folder';
      path: string;
      name: string;
      modifiedLabel: string;
      typeLabel: string;
      sizeLabel: string;
    }
  | {
      kind: 'file';
      path: string;
      name: string;
      modifiedLabel: string;
      typeLabel: string;
      sizeLabel: string;
      file: FileMetadata;
    };

const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);
const preferredQuickAccessFolders = ['Desktop', 'Documents', 'Downloads', 'Pictures', 'Music', 'Videos'];

const normalizePath = (path: string) => {
  if (/^[A-Za-z]:\\$/.test(path)) {
    return path;
  }

  return path.replace(/\\+$/, '');
};

const getFileName = (path: string) => normalizePath(path).split('\\').pop() || path;
const getExtension = (path: string) => getFileName(path).split('.').pop()?.toLowerCase() || '';

const getParentPath = (path: string) => {
  const normalized = normalizePath(path);
  if (/^[A-Za-z]:\\$/.test(normalized)) {
    return null;
  }

  const lastSlashIndex = normalized.lastIndexOf('\\');
  if (lastSlashIndex < 0) {
    return null;
  }

  const parent = normalized.slice(0, lastSlashIndex);
  if (/^[A-Za-z]:$/.test(parent)) {
    return `${parent}\\`;
  }

  return parent || null;
};

const formatFileSize = (size: number) => {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (size >= 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${size} B`;
};

const formatModified = (value?: Date) => {
  if (!value) {
    return '';
  }

  return value.toLocaleString();
};

const getFileTypeLabel = (path: string) => {
  const extension = getExtension(path);
  if (!extension) {
    return 'File';
  }

  if (imageExtensions.has(extension)) {
    return `${extension.toUpperCase()} Image`;
  }

  return `${extension.toUpperCase()} File`;
};

const getBreadcrumbs = (path: string) => {
  const normalized = normalizePath(path);
  const [root, ...rest] = normalized.split('\\').filter(Boolean);
  if (!root) {
    return [] as Array<{ label: string; path: string }>;
  }

  const breadcrumbs = [{ label: root, path: `${root}\\` }];
  let current = `${root}\\`;

  rest.forEach((segment) => {
    current = current.endsWith('\\') ? `${current}${segment}` : `${current}\\${segment}`;
    breadcrumbs.push({ label: segment, path: current });
  });

  return breadcrumbs;
};

const buildFolderPaths = (entries: FileMetadata[]) => {
  const folderSet = new Set<string>();

  entries.forEach((entry) => {
    const normalized = normalizePath(entry.path);
    const parts = normalized.split('\\').filter(Boolean);
    if (parts.length === 0) {
      return;
    }

    let current = `${parts[0]}\\`;
    folderSet.add(current);

    const lastIndex = entry.type === 'folder' ? parts.length - 1 : parts.length - 2;
    for (let index = 1; index <= lastIndex; index += 1) {
      current = current.endsWith('\\') ? `${current}${parts[index]}` : `${current}\\${parts[index]}`;
      folderSet.add(current);
    }
  });

  return Array.from(folderSet).sort((a, b) => a.localeCompare(b));
};

const getInitialFolder = (entries: FileMetadata[], folderPaths: string[]) => {
  const newestFile = [...entries]
    .filter((entry) => entry.type === 'file')
    .sort((a, b) => b.modified.getTime() - a.modified.getTime())[0];

  if (newestFile) {
    return getParentPath(newestFile.path) || folderPaths[0] || 'C:\\';
  }

  return folderPaths[0] || 'C:\\';
};

export default function MapleOSFilePickerDialog({
  open,
  onOpenChange,
  onSelectFile,
  selecting = false,
}: MapleOSFilePickerDialogProps) {
  const [entries, setEntries] = useState<FileMetadata[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('C:\\');
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const loadEntries = async () => {
      setLoading(true);
      try {
        const allEntries = await globalVFS.getAllFiles();
        if (cancelled) {
          return;
        }

        const normalizedEntries = allEntries.map((entry) => ({
          ...entry,
          path: normalizePath(entry.path),
          modified: new Date(entry.modified),
        }));

        const folderPaths = buildFolderPaths(normalizedEntries);
        setEntries(normalizedEntries);
        setCurrentFolder((previous) => (folderPaths.includes(previous) ? previous : getInitialFolder(normalizedEntries, folderPaths)));
        setSelectedFilePath(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadEntries();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const folderPaths = useMemo(() => buildFolderPaths(entries), [entries]);

  const quickAccessFolders = useMemo(() => {
    const quickAccess = preferredQuickAccessFolders
      .map((folderName) => {
        const match = folderPaths.find((path) => getFileName(path).toLowerCase() === folderName.toLowerCase());
        return match ? { label: folderName, path: match } : null;
      })
      .filter((value): value is { label: string; path: string } => Boolean(value));

    const roots = folderPaths
      .filter((path) => /^[A-Za-z]:\\$/.test(path))
      .map((path) => ({ label: path.replace('\\', ''), path }));

    return [...quickAccess, ...roots.filter((root) => !quickAccess.some((item) => item.path === root.path))];
  }, [folderPaths]);

  const visibleRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const childFolders = folderPaths
      .filter((path) => getParentPath(path) === currentFolder)
      .map<PickerRow>((path) => ({
        kind: 'folder',
        path,
        name: getFileName(path),
        modifiedLabel: '',
        typeLabel: 'File folder',
        sizeLabel: '',
      }));

    const childFiles = entries
      .filter((entry) => entry.type === 'file' && getParentPath(entry.path) === currentFolder)
      .sort((a, b) => a.path.localeCompare(b.path))
      .map<PickerRow>((file) => ({
        kind: 'file',
        path: file.path,
        name: getFileName(file.path),
        modifiedLabel: formatModified(file.modified),
        typeLabel: getFileTypeLabel(file.path),
        sizeLabel: formatFileSize(file.size),
        file,
      }));

    const rows = [...childFolders, ...childFiles];
    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) => row.name.toLowerCase().includes(normalizedSearch) || row.path.toLowerCase().includes(normalizedSearch));
  }, [currentFolder, entries, folderPaths, searchTerm]);

  const selectedFile = useMemo(
    () => entries.find((entry) => entry.type === 'file' && entry.path === selectedFilePath) || null,
    [entries, selectedFilePath]
  );

  const breadcrumbs = useMemo(() => getBreadcrumbs(currentFolder), [currentFolder]);

  const handleRowClick = (row: PickerRow) => {
    if (row.kind === 'folder') {
      setSelectedFilePath(null);
      return;
    }

    setSelectedFilePath(row.path);
  };

  const handleRowDoubleClick = (row: PickerRow) => {
    if (row.kind === 'folder') {
      setCurrentFolder(row.path);
      setSelectedFilePath(null);
      return;
    }

    onSelectFile(row.file);
  };

  const handleConfirmSelection = () => {
    if (!selectedFile) {
      return;
    }

    onSelectFile(selectedFile);
  };

  const navigateUp = () => {
    const parent = getParentPath(currentFolder);
    if (!parent) {
      return;
    }

    setCurrentFolder(parent);
    setSelectedFilePath(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">Attach from MapleOS</DialogTitle>

        <div className="border-b bg-muted/30 px-4 py-3">
          <div className="text-base font-semibold text-foreground">Attach from MapleOS</div>
          <div className="text-sm text-muted-foreground">Choose a file from your MapleOS filesystem.</div>
        </div>

        <div className="flex min-h-[560px]">
          <aside className="w-56 border-r bg-muted/20 px-2 py-3">
            <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Quick access
            </div>
            <div className="space-y-1">
              {quickAccessFolders.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                    currentFolder === item.path && 'bg-accent text-accent-foreground'
                  )}
                  onClick={() => {
                    setCurrentFolder(item.path);
                    setSelectedFilePath(null);
                  }}
                >
                  <Folder className="h-4 w-4 text-amber-500" />
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={navigateUp} disabled={!getParentPath(currentFolder)}>
                  Up
                </Button>
                <div className="flex min-w-0 flex-1 items-center rounded-md border bg-background px-2 py-1.5 text-sm">
                  <HardDrive className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                    {breadcrumbs.map((crumb, index) => (
                      <div key={crumb.path} className="flex min-w-0 items-center gap-1">
                        {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        <button
                          type="button"
                          className="truncate rounded px-1 py-0.5 hover:bg-accent hover:text-accent-foreground"
                          onClick={() => {
                            setCurrentFolder(crumb.path);
                            setSelectedFilePath(null);
                          }}
                        >
                          {crumb.label}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative w-72 shrink-0">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search this folder"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_180px_140px_110px] border-b bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Name</div>
              <div>Date modified</div>
              <div>Type</div>
              <div>Size</div>
            </div>

            <ScrollArea className="flex-1 bg-background">
              {loading ? (
                <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading MapleOS files...
                </div>
              ) : visibleRows.length === 0 ? (
                <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
                  {entries.length === 0 ? 'No MapleOS files found.' : 'This folder is empty.'}
                </div>
              ) : (
                <div>
                  {visibleRows.map((row) => {
                    const isSelected = row.kind === 'file' && row.path === selectedFilePath;
                    const isImage = row.kind === 'file' && imageExtensions.has(getExtension(row.path));

                    return (
                      <button
                        key={row.path}
                        type="button"
                        className={cn(
                          'grid w-full grid-cols-[minmax(0,1fr)_180px_140px_110px] items-center px-4 py-2 text-left text-sm transition-colors hover:bg-accent/60',
                          isSelected && 'bg-accent text-accent-foreground',
                          selecting && 'pointer-events-none opacity-60'
                        )}
                        onClick={() => handleRowClick(row)}
                        onDoubleClick={() => handleRowDoubleClick(row)}
                        disabled={selecting}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          {row.kind === 'folder' ? (
                            <Folder className="h-4 w-4 shrink-0 text-amber-500" />
                          ) : isImage ? (
                            <FileImage className="h-4 w-4 shrink-0 text-sky-500" />
                          ) : (
                            <File className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate">{row.name}</span>
                        </div>
                        <div className="truncate text-muted-foreground">{row.modifiedLabel}</div>
                        <div className="truncate text-muted-foreground">{row.typeLabel}</div>
                        <div className="truncate text-muted-foreground">{row.sizeLabel}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            <div className="border-t bg-muted/20 px-4 py-3">
              <div className="grid grid-cols-[72px_minmax(0,1fr)_120px_140px] items-center gap-3">
                <div className="text-sm text-muted-foreground">File name:</div>
                <Input value={selectedFile ? getFileName(selectedFile.path) : ''} readOnly placeholder="Select a file" />
                <div className="text-sm text-muted-foreground">All files (*.*)</div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={selecting}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleConfirmSelection} disabled={!selectedFile || selecting}>
                    Attach
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
